import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../Header';
import Sidebar from '../Sidebar';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_API_URL;

type FileItem = {
  file_id: number;
  document_id?: number;
  administrator_id: number;
  file_type: string;
  description: string;
  file_path: string;
  created_at: string;
  uploaded_at?: string;
  file_name?: string;
  file_size?: number;
  mime_type?: string;
};

type Note = {
  note_id: number;
  administrator_id: number;
  note_text: string;
  created_at: string;
};

interface AdministratorData {
  id: number;
  status: 'Active' | 'Inactive';
  name: string;
  consultantReference: string;
  consultantName: string;
  consultantSurname: string;
  consultantPhone: string;
  consultantEmail: string;
  taxNumber?: string;
  quotationsEmail?: string;
  claimsEmail?: string;
  instructionsEmail?: string;
  createdAt?: string;
  updatedAt?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  region?: string;
  suburb?: string;
  postalCode?: string;
  country?: string;
  emailType?: string;
  adminPhoneNumber?: string;
  adminEmailAddress?: string;
  adminEmailType?: string;
  notes?: Note[];
  files?: FileItem[];
}

const AdministratorDetail = () => {
  const { can } = usePermissions();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [administrator, setAdministrator] = useState<AdministratorData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  // Address form state
  const [addressForm, setAddressForm] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    region: "",
    suburb: "",
    postalCode: "",
    country: "",
  });
  const [isEditingAddress, setIsEditingAddress] = useState(false);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    adminPhoneNumber: "",
    adminEmailAddress: "",
    adminEmailType: "",
    });
  const [isAddingContact, setIsAddingContact] = useState(false);

  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [notes, setNotes] = useState<Note[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [fileType, setFileType] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    const fetchAdministratorDetails = async () => {
      if (!id) {
        setError('No administrator ID provided');
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Debug: log the exact URL being called
        const url = `${API_URL}/api/administrators/${id}`;
        console.log("🔄 Fetching administrator from:", url);
        
        const response = await axios.get(url);
        console.log("✅ Administrator response received:", response.status, response.data);
        
        // Handle different response structures
        const adminData = response.data.data || response.data;
        
        if (!adminData) {
          throw new Error('No data received from server');
        }

        // Normalize createdAt / updatedAt
        const normalizedData = {
          ...adminData,
          createdAt: adminData.created_at || adminData.createdAt,
          updatedAt: adminData.updated_at || adminData.updatedAt
        };
        
        // Remove the old keys
        delete normalizedData.created_at;
        delete normalizedData.updated_at;

        // Initialize contact form state
        setContactForm({
          adminPhoneNumber: normalizedData.adminPhoneNumber || "",
          adminEmailAddress: normalizedData.adminEmailAddress || "",
          adminEmailType: normalizedData.adminEmailType || "",
        });

        console.log("📊 Processed admin data:", normalizedData);
        
        // Fetch files separately
        console.log("📁 Fetching files...");
        try {
          const filesResponse = await axios.get(`${API_URL}/api/administrators/${id}/files`);
          console.log("✅ Files response:", filesResponse.data);
          
          // Handle different response structures for files
          const filesData = filesResponse.data.data || filesResponse.data || filesResponse.data.files || [];
          normalizedData.files = filesData;
          
          console.log("📋 Files data:", filesData);
        } catch (fileError) {
          console.error("❌ Error fetching files:", fileError);
          normalizedData.files = []; // Set empty array if files fail to load
        }

         // Fetch notes separately
      console.log("📝 Fetching notes...");
        try {
          const notesResponse = await axios.get(`${API_URL}/api/administrators/${id}/notes`);
          console.log("✅ Notes response:", notesResponse.data);

          // Handle different response structures for notes
          let notesData = [];
          
          if (Array.isArray(notesResponse.data)) {
            notesData = notesResponse.data; // Direct array response
          } else if (notesResponse.data.data && Array.isArray(notesResponse.data.data)) {
            notesData = notesResponse.data.data; // { data: [] } format
          } else if (notesResponse.data.notes && Array.isArray(notesResponse.data.notes)) {
            notesData = notesResponse.data.notes; // { notes: [] } format
          } else if (Array.isArray(notesResponse.data.results)) {
            notesData = notesResponse.data.results; // { results: [] } format
          }
          
          console.log("📋 Processed notes data:", notesData);
          setNotes(notesData);
          
        } catch (notesError) {
          console.error("❌ Error fetching notes:", notesError);
          if (axios.isAxiosError(notesError)) {
            console.error("Notes error details:", {
              status: notesError.response?.status,
              data: notesError.response?.data
            });
          }
          setNotes([]);
        }

        setAdministrator(normalizedData);

        // Initialize address form
        setAddressForm({
          addressLine1: normalizedData.addressLine1 || "",
          addressLine2: normalizedData.addressLine2 || "",
          city: normalizedData.city || "",
          region: normalizedData.region || "",
          suburb: normalizedData.suburb || "",
          postalCode: normalizedData.postalCode || "",
          country: normalizedData.country || "",
        });

      } catch (err) {
        // Enhanced error handling
        if (axios.isAxiosError(err)) {
          console.error('❌ Axios error:', {
            message: err.message,
            status: err.response?.status,
            url: err.config?.url,
            data: err.response?.data
          });
          
          if (err.response?.status === 404) {
            setError(`Administrator with ID ${id} not found.`);
          } else if (err.response?.status === 500) {
            setError('Server error. Please try again later.');
          } else {
            setError(`Error: ${err.message}`);
          }
        } else {
          setError('An unexpected error occurred.');
          console.error('Unexpected error:', err);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchAdministratorDetails();
  }, [id]);

  // Address change handler
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAddressForm((prev) => ({ ...prev, [name]: value }));
  };

  // Save address handler
  const handleSaveAddress = async () => {
    try {
      await axios.put(`${API_URL}/api/administrators/${id}`, {
        address_line1: addressForm.addressLine1,
        address_line2: addressForm.addressLine2,
        city_town: addressForm.city,
        region: addressForm.region,
        suburb: addressForm.suburb,
        postal_code: addressForm.postalCode,
        country: addressForm.country,
      });

      setAdministrator((prev) =>
        prev ? { ...prev, ...addressForm } : prev
      );
      setIsEditingAddress(false);
    } catch (err) {
      console.error("Error saving address:", err);
    }
  };

  // Cancel address edit handler
  const handleCancelAddress = () => {
    if (!administrator) return;
    setAddressForm({
      addressLine1: administrator.addressLine1 || "",
      addressLine2: administrator.addressLine2 || "",
      city: administrator.city || "",
      region: administrator.region || "",
      suburb: administrator.suburb || "",
      postalCode: administrator.postalCode || "",
      country: administrator.country || "",
    });
    setIsEditingAddress(false);
  };

  // Start fresh form for adding new address handler
  const handleAddNewAddress = () => {
    setAddressForm({
      addressLine1: "",
      addressLine2: "",
      city: "",
      region: "",
      suburb: "",
      postalCode: "",
      country: "",
    });
    setIsEditingAddress(true);
  };

  //save new contact handler
  const handleSaveContact = async () => {
    try {
      // Only include non-empty fields
      const payload: any = {};
      if (contactForm.adminPhoneNumber) payload.adminPhoneNumber = contactForm.adminPhoneNumber;
      if (contactForm.adminEmailAddress) payload.adminEmailAddress = contactForm.adminEmailAddress;
      if (contactForm.adminEmailType) payload.adminEmailType = contactForm.adminEmailType;


      if (Object.keys(payload).length === 0) {
        console.warn("No contact fields to update");
        return;
      }

      console.log("Saving contact with data:", payload); // Debug

      await axios.put(`${API_URL}/api/administrators/${id}`, payload);

      // Update frontend state
      setAdministrator((prev) =>
        prev
          ? {
              ...prev,
              adminPhoneNumber: contactForm.adminPhoneNumber,
              adminEmailAddress: contactForm.adminEmailAddress,
              adminEmailType: contactForm.adminEmailType,
            }
          : prev
      );
      setIsAddingContact(false);
    } catch (err) {
      console.error("Error saving contact:", err);
    }
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) return;
    
    try {
      console.log("💾 Saving note:", noteText);
      const response = await axios.post(`${API_URL}/api/administrators/${id}/notes`, {
        noteText,
      });

      console.log("✅ Note save response:", response.data);
      
      // Handle different response structures
      const newNote = response.data.data || response.data || response.data.note;
      
      if (newNote) {
        // Append new note to local state
        setNotes((prev) => [...prev, newNote]);
        setNoteText("");
        setIsAddingNote(false);
        alert("Note saved successfully!");
      } else {
        throw new Error("Invalid note response from server");
      }
      
    } catch (err) {
      console.error("❌ Error saving note:", err);
      alert("Failed to save note. Check console for details.");
    }
  };

  // File Upload Handler
  const handleFileUpload = async () => {
    if (!selectedFile || !fileType || !description) {
      alert("Please fill all fields and select a file before uploading.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("fileType", fileType);
      formData.append("description", description);

      await axios.post(`${API_URL}/api/administrators/${id}/files`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // reset form
      setFileType("");
      setDescription("");
      setSelectedFile(null);
      setIsUploadingFile(false);

      // re-fetch file list
      const filesResponse = await axios.get(`${API_URL}/api/administrators/${id}/files`);
      console.log("Fetched administrator files:", filesResponse.data);
      setAdministrator((prev) => prev ? { ...prev, files: filesResponse.data.data } : prev);

    } catch (err) {
      console.error("Error uploading file:", err);
      alert("Failed to upload file.");
    }
  };

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <p>Loading administrator details...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="min-h-screen flex items-center justify-center text-red-600">
          <p>Error: {error}</p>
          <Link to="/administrators" className="ml-4 text-blue-600 hover:underline">
            Back to Administrators
          </Link>
        </div>
      );
    }

  if (!administrator) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Administrator not found.</p>
        <Link to="/administrators" className="ml-4 text-blue-600 hover:underline">
          Back to Administrators
        </Link>
      </div>
    );
  }

  //if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex relative">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />
      
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title="Administrator Details"
          user={user}
          client={null}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />
        
        <div className="flex-1 p-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            {/* Back button */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/administrators')}
                className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to Administrators
              </button>
            </div>

            {/* ✅ Big Administrator Name */}
            {administrator && (
              <h1 className="text-3xl font-bold text-gray-900 mb-6">
                {administrator.name}
              </h1>
            )}

            {/* Edit Administrator Button*/}
            {can('insurers', 'write') && (
              <div className="flex justify-end mb-6">
                <button
                  onClick={() => navigate(`/administrators/${administrator.id}/edit`)}
                  className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg"
                >
                  Edit Administrator
                </button>
              </div>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-8">
                {['profile', 'address', 'contacts', 'notes', 'files'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="space-y-4">
              {activeTab === 'profile' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                    <h3 className="text-lg font-semibold mb-4">Administrator Information</h3>
                    <div className="space-y-4">
                        <div key="name">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Name</label>
                        <p className="text-gray-900">{administrator.name}</p>
                        </div>

                        <div key="status">
                          <label className="text-sm font-medium text-gray-500 block mb-1">Status</label>
                          <p className="text-gray-900">
                            {administrator.status}
                          </p>
                        </div>

                        <div key="reference">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Broker Code</label>
                        <p className="text-gray-900">{administrator.consultantReference}</p>
                        </div>
                        <div key="tax">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Tax Number</label>
                        <p className="text-gray-900">{administrator.taxNumber || 'Not provided'}</p>
                        </div>
                        <div key="fullname">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Consultant Full Name</label>
                        <p className="text-gray-900">{administrator.consultantName} {administrator.consultantSurname}</p>
                        </div>
                    </div>
                    </div>
                    
                    <div>
                    <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                    <div className="space-y-4">
                        <div key="consultantEmail">
                          <label className="text-sm font-medium text-gray-500 block mb-1">Consultant Email</label>
                          <p className="text-blue-600">
                              <a href={`mailto:${administrator.consultantEmail}`}>{administrator.consultantEmail}</a>
                          </p>
                        </div>

                        <div key="consultantPhone">
                          <label className="text-sm font-medium text-gray-500 block mb-1">Consultant Phone Number</label>
                          <p className="text-gray-900">{administrator.consultantPhone}</p>
                        </div>

                        <div key="quotationsEmail">
                          <label className="text-sm font-medium text-gray-500 block mb-1">Quotations Email</label>
                          <p className="text-blue-600">
                              <a href={`mailto:${administrator.quotationsEmail || 'quotations@example.com'}`}>
                              {administrator.quotationsEmail || 'quotations@example.com'}
                              </a>
                          </p>
                        </div>

                        <div key="claimsEmail">
                          <label className="text-sm font-medium text-gray-500 block mb-1">Claims Email</label>
                          <p className="text-blue-600">
                              <a href={`mailto:${administrator.claimsEmail || 'claims@example.com'}`}>
                              {administrator.claimsEmail || 'claims@example.com'}
                              </a>
                          </p>
                        </div>

                        <div key="instructionsEmail">
                          <label className="text-sm font-medium text-gray-500 block mb-1">Instructions Email</label>
                          <p className="text-blue-600">
                              <a href={`mailto:${administrator.instructionsEmail || 'instructions@example.com'}`}>
                              {administrator.instructionsEmail || 'instructions@example.com'}
                              </a>
                          </p>
                        </div>

                      </div>
                    </div>
                </div>
                )}

              {/* Address Tab */}
              {activeTab === 'address' && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Address Information</h3>

                  {!isEditingAddress ? (
                    <div className="bg-gray-50 p-6 rounded-xl shadow-sm border border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div key="address1">
                          <p className="text-sm text-gray-500">Address Line 1</p>
                          <p className="text-lg font-medium text-gray-900">{administrator.addressLine1}</p>
                        </div>
                        <div key="address2">
                          <p className="text-sm text-gray-500">Address Line 2</p>
                          <p className="text-lg font-medium text-gray-900">{administrator.addressLine2 || "Not provided"}</p>
                        </div>
                        <div key="city">
                          <p className="text-sm text-gray-500">City / Town</p>
                          <p className="text-lg font-medium text-gray-900">{administrator.city || "Not provided"}</p>
                        </div>
                        <div key="region">
                          <p className="text-sm text-gray-500">Region</p>
                          <p className="text-lg font-medium text-gray-900">{administrator.region || "Not provided"}</p>
                        </div>
                        <div key="suburb">
                          <p className="text-sm text-gray-500">Suburb</p>
                          <p className="text-lg font-medium text-gray-900">{administrator.suburb || "Not provided"}</p>
                        </div>
                        <div key="postalCode">
                          <p className="text-sm text-gray-500">Postal Code</p>
                          <p className="text-lg font-medium text-gray-900">{administrator.postalCode || "Not provided"}</p>
                        </div>
                        <div key="country">
                          <p className="text-sm text-gray-500">Country</p>
                          <p className="text-lg font-medium text-gray-900">{administrator.country || "Not provided"}</p>
                        </div>
                      </div>

                      {/* Buttons */}
                      {can('insurers', 'write') && (
                        <div className="flex space-x-3 mt-6">
                          {administrator.addressLine1 ? (
                            // Show only Edit if an address exists
                            <button
                              onClick={() => setIsEditingAddress(true)}
                              className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-lg shadow"
                            >
                              Edit Address
                            </button>
                          ) : (
                            // Show Add New Address if no address exists
                            <button
                              onClick={handleAddNewAddress}
                              className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-lg shadow"
                            >
                              Add New Address
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                  ) : (
                    <form className="space-y-4">
                      {/* Address form fields (same as your edit version) */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
                          <input
                            type="text"
                            name="addressLine1"
                            value={addressForm.addressLine1}
                            onChange={handleAddressChange}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
                          <input
                            type="text"
                            name="addressLine2"
                            value={addressForm.addressLine2}
                            onChange={handleAddressChange}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">City/Town</label>
                          <input
                            type="text"
                            name="city"
                            value={addressForm.city}
                            onChange={handleAddressChange}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Region</label>
                          <input
                            type="text"
                            name="region"
                            value={addressForm.region}
                            onChange={handleAddressChange}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Suburb</label>
                          <input
                            type="text"
                            name="suburb"
                            value={addressForm.suburb}
                            onChange={handleAddressChange}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                          <input
                            type="text"
                            name="postalCode"
                            value={addressForm.postalCode}
                            onChange={handleAddressChange}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Country</label>
                          <input
                            type="text"
                            name="country"
                            value={addressForm.country}
                            onChange={handleAddressChange}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={handleCancelAddress}
                          className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded-lg"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveAddress}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg"
                        >
                          Save
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {/* Contacts Tab */}
                {activeTab === "contacts" && (
                  <div className="p-6 space-y-4">
                    <h2 className="text-xl font-semibold">Contact Information</h2>

                    {/* Show contact info if available */}
                    {administrator && (administrator.adminPhoneNumber || administrator.adminEmailAddress || administrator.adminEmailType) ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <p key="phone"><strong>Phone Number:</strong> {administrator.adminPhoneNumber || "—"}</p>
                        <p key="email"><strong>Email:</strong> {administrator.adminEmailAddress || "—"}</p>
                        <p key="emailType"><strong>Email Type:</strong> {administrator.adminEmailType || "—"}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500">No contact information available</p>
                    )}


                    {/* Add or Edit button */}
                    {can('insurers', 'write') && !isAddingContact && (
                      <>
                        {administrator && (administrator.adminPhoneNumber || administrator.adminEmailAddress || administrator.adminEmailType) ? (
                          <button
                            onClick={() => {
                              // Pre-fill form with existing values
                              setContactForm({
                                adminPhoneNumber: administrator.adminPhoneNumber || "",
                                adminEmailAddress: administrator.adminEmailAddress || "",
                                adminEmailType: administrator.adminEmailType || "",
                              });
                              setIsAddingContact(true);
                            }}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-2 rounded-lg"
                          >
                            Edit Contact
                          </button>
                        ) : (
                          <button
                            onClick={() => setIsAddingContact(true)}
                            className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-lg"
                          >
                            Add Contact
                          </button>
                        )}
                      </>
                    )}

                    {/* Contact form for add/edit */}
                    {isAddingContact && (
                      <div className="space-y-3 bg-gray-50 p-4 rounded-lg shadow">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <input
                            type="text"
                            placeholder="Phone Number"
                            value={contactForm.adminPhoneNumber}
                            onChange={(e) =>
                              setContactForm({ ...contactForm, adminPhoneNumber: e.target.value })
                            }
                            className="w-full border rounded-lg px-3 py-2"
                          />
                          <input
                            type="email"
                            placeholder="Email Address"
                            value={contactForm.adminEmailAddress}
                            onChange={(e) =>
                              setContactForm({ ...contactForm, adminEmailAddress: e.target.value })
                            }
                            className="w-full border rounded-lg px-3 py-2"
                          />
                          <select
                            value={contactForm.adminEmailType}
                            onChange={(e) =>
                              setContactForm({ ...contactForm, adminEmailType: e.target.value })
                            }
                            className="w-full border rounded-lg px-3 py-2"
                          >
                            <option value="">Select Email Type</option>
                            <option value="Personal">Personal</option>
                            <option value="Business">Business</option>
                          </select>
                        </div>

                        <div className="flex gap-3 mt-4">
                          <button
                            onClick={handleSaveContact}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-lg"
                          >
                            Save Contact
                          </button>
                          <button
                            onClick={() => setIsAddingContact(false)}
                            className="bg-gray-400 hover:bg-gray-500 text-white px-5 py-2 rounded-lg"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                  </div>
                )}

              {/* Notes Tab */}
              {activeTab === "notes" && (
                <div className="p-6 space-y-4">
                  <h3 className="text-lg font-semibold mb-4">Notes</h3>

                  {/* Add note button */}
                  {can('insurers', 'write') && !isAddingNote && (
                    <button
                      onClick={() => setIsAddingNote(true)}
                      className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-lg"
                    >
                      Add Note
                    </button>
                  )}
                  
                  {/* Note input form */}
                  {isAddingNote && (
                    <div className="space-y-3 bg-gray-50 p-4 rounded-lg shadow mb-4">
                      <textarea
                        placeholder="Enter your note..."
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                        rows={4}
                      />
                      <div className="flex gap-3">
                        <button
                          onClick={handleSaveNote}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-lg"
                        >
                          Save Note
                        </button>
                        <button
                          onClick={() => setIsAddingNote(false)}
                          className="bg-gray-400 hover:bg-gray-500 text-white px-5 py-2 rounded-lg"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Display notes */}
                  {notes.length > 0 ? (
                    <div className="space-y-3">
                      {notes.map((note) => (
                        <div
                          key={note.note_id}
                          className="p-4 bg-white rounded-lg shadow border border-gray-200"
                        >
                          <p>{note.note_text}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(note.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No notes available.</p>
                  )}
                </div>
              )}



              {/* Files Tab */}
              {activeTab === "files" && (
                <div className="p-6 space-y-4">
                  <h3 className="text-lg font-semibold mb-4">Files & Documents</h3>

                  {can('insurers', 'write') && !isUploadingFile && (
                    <button
                      onClick={() => setIsUploadingFile(true)}
                      className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-lg"
                    >
                      Create Document
                    </button>
                  )}

                  {isUploadingFile && (
                    <div className="space-y-4 bg-gray-50 p-6 rounded-lg shadow">
                      {/* File Type Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          File Type
                        </label>
                        <select
                          value={fileType}
                          onChange={(e) => setFileType(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2"
                        >
                          <option value="">Select File Type</option>
                          <option value="Company Registration Documents">
                            Company Registration Documents
                          </option>
                          <option value="Identification Documents">
                            Identification Documents
                          </option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      {/* Description Input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2"
                          placeholder="Enter description"
                        />
                      </div>

                      {/* File Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Upload Document
                        </label>
                        <input
                          type="file"
                          onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                          className="w-full"
                        />
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={handleFileUpload}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-lg"
                        >
                          Upload
                        </button>
                        <button
                          onClick={() => setIsUploadingFile(false)}
                          className="bg-gray-400 hover:bg-gray-500 text-white px-5 py-2 rounded-lg"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Display uploaded files */}
                  {administrator.files && administrator.files.length > 0 ? (
                    <div className="mt-6">
                      <h4 className="text-md font-semibold mb-2">Uploaded Files</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full border border-gray-200 rounded-lg">
                          <thead className="bg-gray-100">
                            <tr key="header">
                              <th className="text-left p-2 border-b">File Type</th>
                              <th className="text-left p-2 border-b">Description</th>
                              <th className="text-left p-2 border-b">Uploaded</th>
                              <th className="text-left p-2 border-b">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {administrator.files.map((file) => (
                              <tr key={file.file_id || file.document_id || `file-${file.file_type}-${file.description}`} className="border-b">
                                <td className="p-2">{file.file_type}</td>
                                <td className="p-2">{file.description}</td>
                                <td className="p-2 text-gray-500">
                                  {new Date(file.created_at).toLocaleDateString()}
                                </td>
                                <td className="p-2">
                                  <a
                                    href={`${API_URL}/${file.file_path}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    Download
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 mt-4">No files available.</p>
                  )}


                </div>
              )}


            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdministratorDetail;